/**
 * Chain registry — typed frontend wrapper over the shared single source of truth
 * (`shared/config/chains.js`). This is the canonical FE entry point for anything
 * chain-aware: badges, the chain selector, OG/recaps, per-chain SEO, the admin
 * chain filter, etc. The backend requires the same `.js` directly.
 *
 * Do NOT redefine chain data here — it lives in `shared/config/chains.js` so the FE
 * and BE share one truth. This file only adds TypeScript types + ergonomic helpers.
 *
 * See docs/superpowers/specs/2026-06-05-chain-first-class-surfaces.md for field semantics.
 */

import {
  CHAINS as RAW_CHAINS,
  CHAIN_IDS as RAW_CHAIN_IDS,
  DEFAULT_CHAIN_ID as RAW_DEFAULT_CHAIN_ID,
  getChain as rawGetChain,
  listChains as rawListChains,
  selectableChainIds as rawSelectableChainIds,
  isChainId as rawIsChainId,
  getChainLabel as rawGetChainLabel,
  recapReadyChainIds as rawRecapReadyChainIds,
} from '@shared/config/chains.js';

/** Public display state of a chain. */
export type ChainStatus = 'live' | 'mvc' | 'planned';

/** Chain ids are strings (validated at runtime via `isChainId`). */
export type ChainId = string;

/** How to populate a chain's data — slugs consumed by the data services. */
export interface ChainFeeds {
  /** DefiLlama chain slug (e.g. 'Abstract'). */
  tvl?: string;
  /** DexScreener chain path segment (e.g. 'abstract'). */
  tokens?: string;
  /** OpenSea/NFT chain slug (e.g. 'abstract'). */
  nft?: string;
  /** CoinGecko asset-platform id (e.g. 'abstract'). */
  price?: string;
}

export interface ChainMeta {
  id: ChainId;
  /** UI label, e.g. "Base". */
  label: string;
  /** Brand accent (hex). `ChainBadge` derives its styling from this. */
  color: string;
  /** Public logo asset, e.g. "/chain-base.png" (PNG — Satori OG can't embed webp). */
  logo: string;
  /** Per-chain recap OG card asset. */
  ogCard: string;
  /** Public display state. */
  status: ChainStatus;
  /** Authoring gate — may an operator pick this in the tag modal? (distinct from status) */
  selectable: boolean;
  /**
   * Is this a covered-or-candidate Descout ecosystem — i.e. does it belong in the
   * ecosystems lens switcher? Distinct from `status` and `selectable`: Ethereum is
   * selectable (cross-chain entities live there) but isn't an ecosystem we cover.
   */
  ecosystem?: boolean;
  /** Route segment for the per-chain hub `/<hubSlug>` (may differ from id). */
  hubSlug: string;
  feeds: ChainFeeds;
  /** One-liner for the Ecosystems rail card + hub header. */
  tagline?: string;
  /** MVC thin-state card copy ("coverage just launched — tracking …"). */
  thinStateMessage?: string;
  /** SEO/JSON-LD noun where `label` is too bare ("Base" -> "Base (L2)"). */
  seoLabel?: string;
  /** Block-explorer base URL (reserved for asset deep-links). */
  explorerUrl?: string;
  /**
   * Chain id this chain settles to (L2 → L1, e.g. Abstract/Base → 'ethereum').
   * Config home for the settles-on fact — a chain is never an entity/typed edge
   * (2026-06-08 ontology decision). Distinct from app→protocol BUILT_ON.
   */
  settlesOn?: ChainId;
  /**
   * Slug of the `organization` entity that operates this chain (e.g. 'coinbase'
   * for Base). Points at an org node; the chain itself stays a non-node.
   */
  operator?: string;
  /**
   * K9/EO3 — per-chain copy for the compiled `knowledge_pages` lens overview
   * (backend/src/services/lens-page-ingest.js). `ecosystemLabel` names the cluster in the
   * page title/heading ("Abstract gaming ecosystem"); `coreClause` is the trailing lead-sentence
   * characterization ("…, with gaming projects at its core."). Omitted → neutral default (no
   * "gaming" leakage). Consumed via `getChainLensConfig`.
   */
  lens?: { ecosystemLabel: string; coreClause: string };
}

/** The full registry, keyed by chain id (in display order). */
export const CHAINS: Record<ChainId, ChainMeta> = RAW_CHAINS as Record<ChainId, ChainMeta>;

/** All chain ids, in registry (display) order. */
export const CHAIN_IDS: ChainId[] = RAW_CHAIN_IDS as ChainId[];

/**
 * The default/home chain — the one chain every surface covered before Descout went
 * multi-chain. Re-exported from the shared registry so FE code that needs an
 * Abstract-back-compat fallback (e.g. a chain-scoped component rendered without a lens)
 * names the concept instead of re-hardcoding the `'abstract'` string.
 */
export const DEFAULT_CHAIN_ID: ChainId = RAW_DEFAULT_CHAIN_ID as ChainId;

/** Safe, case-insensitive lookup. Returns undefined for unknown/blank ids. */
export function getChain(id: string | undefined | null): ChainMeta | undefined {
  return rawGetChain(id) as ChainMeta | undefined;
}

/** All chain metas, in registry order. */
export function listChains(): ChainMeta[] {
  return rawListChains() as ChainMeta[];
}

/** Metas an operator may assign in the tag modal (selectable === true). */
export function selectableChains(): ChainMeta[] {
  return selectableChainIds().map((id) => CHAINS[id]);
}

/** Ids an operator may assign as an entity `chains[]` value (the modal allowlist). */
export function selectableChainIds(): ChainId[] {
  return rawSelectableChainIds() as ChainId[];
}

/** Type guard — is `id` a known chain id? */
export function isChainId(id: unknown): id is ChainId {
  return rawIsChainId(id);
}

/** Display label for a chain id, or undefined if unknown. */
export function getChainLabel(id: string | undefined | null): string | undefined {
  return rawGetChainLabel(id) as string | undefined;
}

/**
 * Chains whose daily-recap data inputs are verified (`recapDataReady: true`), default chain
 * first. Fail-closed — a chain that has not been through the enable checklist is absent.
 *
 * The FRONTEND uses it to size read windows: every chain scope over `ecosystem_recaps` runs
 * IN MEMORY (pre-multichain docs carry no `chain` field, so a query-side filter matches
 * none of them), which means a query has to over-fetch by however many chains can interleave
 * in one `date desc` stream. Hard-coding 2 there would silently truncate the day a third
 * chain lands.
 */
export function recapReadyChainIds(): ChainId[] {
  return rawRecapReadyChainIds() as ChainId[];
}

/**
 * The HOME chain of an entity = the first entry in its `chains[]` (decision 2026-06-05).
 * Drives the OG/TVL stamp and the SEO canonical. Returns undefined for chain-agnostic
 * descriptor tags (no `chains`) or unknown ids.
 */
export function getHomeChain(chains: string[] | undefined | null): ChainMeta | undefined {
  if (!chains || chains.length === 0) return undefined;
  return getChain(chains[0]);
}

/** SEO label for a chain: explicit `seoLabel`, else `"<Label> blockchain"`. */
export function getChainSeoLabel(id: string | undefined | null): string | undefined {
  const c = getChain(id);
  if (!c) return undefined;
  return c.seoLabel ?? `${c.label} blockchain`;
}
